import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import { templateAccess } from "./meetingTemplateForm";

export default function MeetingTemplateImport() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { canAdvisory, canTeam, canList } = templateAccess(user);
  const typeParam = params.get("type");
  const sessionType = typeParam === "advisory" && canAdvisory ? "advisory" : (canTeam ? "internal_1_1" : "advisory");
  const [blank, setBlank] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!canList) return;
    api.get(`/meeting-templates/markdown-template?lang=${lang}&session_type=${sessionType}`)
      .then((r) => setBlank(r.data.text || ""))
      .catch(() => setBlank(""));
  }, [canList, lang, sessionType]);

  if (!canList) {
    return (
      <div className="tb-page">
        <h1 className="tb-h1">{t("settings.templates.importMdTitle")}</h1>
        <p className="tb-muted">{t("settings.templates.principalOnly")}</p>
      </div>
    );
  }

  const listUrl = `/app/settings/templates?type=${sessionType}`;

  const submit = async () => {
    if (!text.trim()) { setErr(t("settings.templates.mdEmpty")); return; }
    setBusy(true);
    setErr("");
    try {
      const r = await api.post("/meeting-templates/import", { text, source_lang: lang, session_type: sessionType });
      navigate(`/app/settings/templates/build/${r.data.id}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setText(await file.text());
  };

  return (
    <div className="tb-page" data-testid="template-md-import">
      <Link to={listUrl} className="tb-muted" style={{ fontSize: "0.85rem" }}>← {t("common.back")}</Link>
      <h1 className="tb-h1" style={{ marginTop: 8 }}>{t("settings.templates.importMdTitle")}</h1>
      <p className="tb-muted" style={{ maxWidth: 760, marginBottom: 28 }}>{t("settings.templates.importMdHint")}</p>

      {err && <div className="tb-form-error" style={{ marginBottom: 12 }}>{err}</div>}

      <div className="tb-block">
        <h3 style={{ marginTop: 0 }}>{t("fwImport.step1Title")}</h3>
        <p className="tb-muted" style={{ marginTop: 0, marginBottom: 20 }}>{t("fwImport.step1Hint")}</p>
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          <button type="button" className="tb-ghost-btn" onClick={() => navigator.clipboard.writeText(blank)}>{t("fwImport.copy")}</button>
          <label className="tb-ghost-btn" style={{ cursor: "pointer" }}>
            {t("settings.templates.pickFile")}
            <input type="file" accept=".md,text/markdown,text/plain" hidden onChange={onPickFile} data-testid="template-md-file" />
          </label>
        </div>
        <textarea
          readOnly
          value={blank}
          rows={12}
          data-testid="template-md-blank"
          style={{ width: "100%", fontFamily: "ui-monospace, monospace", fontSize: "0.82rem", lineHeight: 1.7, padding: "16px 18px", background: "rgba(0,0,0,0.25)", whiteSpace: "pre-wrap" }}
        />
      </div>

      <div className="tb-block" style={{ marginTop: 14 }}>
        <h3 style={{ marginTop: 0 }}>{t("fwImport.step2Title")}</h3>
        <p className="tb-muted" style={{ marginTop: 0, marginBottom: 20 }}>{t("fwImport.step2Hint")}</p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={16}
          placeholder={t("settings.templates.pasteMd")}
          data-testid="template-md-text"
          style={{ width: "100%", fontFamily: "ui-monospace, monospace", fontSize: "0.88rem", lineHeight: 1.7, padding: "16px 18px" }}
        />
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        <button type="button" className="tb-primary-btn" onClick={submit} disabled={busy} data-testid="template-md-submit">
          {busy ? t("fwImport.importing") : t("settings.templates.importCta")}
        </button>
        <Link to={listUrl} className="tb-ghost-btn" style={{ textDecoration: "none" }}>{t("book.cancel")}</Link>
      </div>
    </div>
  );
}
